import React, { useEffect, useState } from "react";
import styles from "./style.module.scss";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  CheckCircleFilled,
  CloseCircleFilled,
  LoadingOutlined,
  ClockCircleOutlined,
  DownOutlined,
  RightOutlined,
} from "@ant-design/icons";

const SUBSTEP_ICON = {
  done:    <CheckCircleFilled  style={{ color: "#52c41a", fontSize: 13 }} />,
  error:   <CloseCircleFilled  style={{ color: "#ff4d4f", fontSize: 13 }} />,
  running: <LoadingOutlined    style={{ color: "#7c5cbf", fontSize: 13 }} spin />,
  pending: <ClockCircleOutlined style={{ color: "#555",   fontSize: 13 }} />,
  skipped: <ClockCircleOutlined style={{ color: "#444",   fontSize: 13 }} />,
};

function fmtDuration(startedAt, finishedAt) {
  if (!startedAt || !finishedAt) return null;
  const s = (new Date(finishedAt) - new Date(startedAt)) / 1000;
  if (s < 60) return `${s.toFixed(1)}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`;
  return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
}

function StepStatusCircle({ id, completed, running, error }) {
  if (completed) {
    return (
      <div className={`${styles.stepNumber} ${styles.completed}`}>
        <img src="/assets/check_circle_outline.svg" alt="done" />
      </div>
    );
  }
  if (error) {
    return (
      <div className={`${styles.stepNumber} ${styles.stepError}`}>
        <CloseCircleFilled style={{ color: "#ff4d4f", fontSize: 16 }} />
      </div>
    );
  }
  if (running) {
    return (
      <div className={`${styles.stepNumber} ${styles.stepRunning}`}>
        <LoadingOutlined spin style={{ color: "#7c5cbf", fontSize: 16 }} />
      </div>
    );
  }
  return (
    <div className={styles.stepNumber}>{id}</div>
  );
}

const JourneyStepItem = ({ step, setSelectedStep, active, id, monolithSteps }) => {
  const [open, setOpen] = useState(false);
  const [isMobileView, setIsMobileView] = useState(false);
  const { t } = useTranslation();
  const theme = useSelector((state) => state.DashboardReducer.theme);

  useEffect(() => {
    const handle = () => setIsMobileView(window.innerWidth < 750);
    handle();
    window.addEventListener("resize", handle);
    return () => window.removeEventListener("resize", handle);
  }, []);

  // Auto-open if any substep is running or errored
  useEffect(() => {
    if (monolithSteps?.some((s) => s.status === "running" || s.status === "error")) {
      setOpen(true);
    }
  }, [monolithSteps]);

  const hasSubsteps = monolithSteps && monolithSteps.length > 0;
  const isRunning = hasSubsteps && monolithSteps.some((s) => s.status === "running");
  const hasError  = hasSubsteps && monolithSteps.some((s) => s.status === "error");
  const allDone   = hasSubsteps && monolithSteps.every((s) => s.status === "done" || s.status === "skipped");
  const completed = !!step?.completedAt || !!step?.time?.length;
  const startedAt = step?.startedAt || "";
  const completedAt = step?.completedAt || step?.time || "";
  const showStarted = !completed && !!startedAt;
  const showCompleted = completed && !!completedAt;

  return (
    <div className={styles.mainContainer}>
      {/* ── left: timestamp ── */}
      <div
        className={styles.stepInfo}
        style={showStarted || showCompleted ? {} : { visibility: "hidden" }}
      >
        <h4>
          {showCompleted
            ? t("journey_page.1")
            : t("journey_page.startedAt", { defaultValue: "Started At" })}
        </h4>
        <div className={styles.info}>
          <div className={styles.item}>
            <img src={theme === "dark" ? "/assets/calendar.svg" : "/assets/calendar_black.svg"} alt="" width={18} height={18} />
            {(showCompleted ? completedAt : startedAt)?.split(",")[0]}
          </div>
          <div className={styles.item}>
            <img src={theme === "dark" ? "/assets/clock.svg" : "/assets/clock_black.svg"} alt="" width={18} height={18} />
            {(showCompleted ? completedAt : startedAt)?.split(",")[1]}
          </div>
        </div>
      </div>

      {/* ── center: circle indicator ── */}
      <StepStatusCircle
        id={id}
        completed={completed}
        running={(isRunning || active) && !completed}
        error={hasError && !completed}
      />

      {/* ── right: card + substep panel ── */}
      <div className={styles.stepContainer}>
        <div
          className={`${styles.container} ${active ? styles.active : ""} ${(isRunning || active) && !completed ? styles.containerRunning : ""}`}
          onClick={() => hasSubsteps && setOpen((v) => !v)}
          style={{ cursor: hasSubsteps ? "pointer" : "default" }}
        >
          <div className={styles.icon}>
            {id === 1 ? <img src="/assets/journey_dashboard.svg" alt="" />
            : id === 2 ? <img src="/assets/journey_data.svg"      alt="" />
            : id === 3 ? <img src="/assets/journey_nlp.svg"       alt="" />
            : id === 4 ? <img src="/assets/journey_topic.svg"     alt="" />
            :             <img src="/assets/journey_results.svg"  alt="" />}
          </div>

          <div className={styles.step}>
            <h5>{step.title}</h5>

            {/* substep summary badge */}
            {hasSubsteps && (
              <div className={styles.substepSummary}>
                {isRunning && !completed && (
                  <span className={styles.badgeRunning}>
                    <LoadingOutlined spin style={{ marginRight: 4 }} />
                    {t("journey_page.inProgress")}
                  </span>
                )}
                {!isRunning && active && !completed && !hasError && (
                  <span className={styles.badgeRunning}>
                    <LoadingOutlined spin style={{ marginRight: 4 }} />
                    {t("journey_page.inProgress")}
                  </span>
                )}
                {hasError && !completed && (
                  <span className={styles.badgeError}>
                    <CloseCircleFilled style={{ marginRight: 4 }} />
                    {t("journey_page.failed")}
                  </span>
                )}
                {allDone && (
                  <span className={styles.badgeDone}>
                    <CheckCircleFilled style={{ marginRight: 4 }} />
                    {monolithSteps.length}/{monolithSteps.length} {t("journey_page.stepsComplete")}
                  </span>
                )}
                {!isRunning && !hasError && !allDone && (
                  <span className={styles.badgePending}>
                    {monolithSteps.filter((s) => s.status === "done").length}/{monolithSteps.length} {t("journey_page.stepsComplete")}
                  </span>
                )}
              </div>
            )}

            {/* old "Dettagli" for non-monolith steps */}
            {!hasSubsteps && (
              <div className={styles.detail} onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}>
                {t("journey_page.18")}
                <i className="fa-solid fas fa-caret-down" style={{ fontSize: "12px", marginLeft: 4 }} />
              </div>
            )}
            {!hasSubsteps && open && step.info && <h6>{step.info}</h6>}
          </div>

          {/* expand chevron */}
          {hasSubsteps && (
            <span className={styles.expandChevron}>
              {open ? <DownOutlined /> : <RightOutlined />}
            </span>
          )}
        </div>

        {/* ── substep detail panel ── */}
        {hasSubsteps && open && (
          <div className={styles.substepPanel}>
            {monolithSteps.map((s) => {
              const dur = fmtDuration(s.started_at, s.finished_at);
              const elapsedRunning = s.status === "running" && s.started_at
                ? (() => {
                    const elapsed = (Date.now() - new Date(s.started_at)) / 1000;
                    if (elapsed < 60) return `${elapsed.toFixed(0)}s`;
                    return `${Math.floor(elapsed / 60)}m ${Math.round(elapsed % 60)}s`;
                  })()
                : null;
              return (
                <div key={s.name} className={`${styles.substep} ${styles[`substep_${s.status}`]}`}>
                  <span className={styles.substepIcon}>{SUBSTEP_ICON[s.status] || SUBSTEP_ICON.pending}</span>
                  <div className={styles.substepBody}>
                    <span className={styles.substepLabel}>{s.label}</span>
                    {s.detail && <span className={styles.substepDetail}>{s.detail}</span>}
                    {s.error  && <span className={styles.substepError}>❗ {s.error}</span>}
                  </div>
                  {(dur || elapsedRunning) && (
                    <span className={styles.substepDuration}>{dur || elapsedRunning}</span>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* mobile timestamp */}
        <div className={styles.stepInfoMobile}>
          {(showStarted || showCompleted) && (
            <h4>
              {showCompleted
                ? t("journey_page.1")
                : t("journey_page.startedAt", { defaultValue: "Started At" })}
            </h4>
          )}
          {(showStarted || showCompleted) && (
            <div className={styles.info}>
              <div className={styles.item}>
                <img src={theme === "dark" ? "/assets/calendar.svg" : "/assets/calendar_black.svg"} alt="" width={18} height={18} />
                {(showCompleted ? completedAt : startedAt).split(",")[0]}
              </div>
              <div className={styles.item}>
                <img src={theme === "dark" ? "/assets/clock.svg" : "/assets/clock_black.svg"} alt="" width={18} height={18} />
                {(showCompleted ? completedAt : startedAt).split(",")[1]}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default JourneyStepItem;
